import React from 'react';
import { SideNavTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * Reports — Round HR-PA (Part 12): FOUR clear categories.
 *
 *   1. FTO Reports    — the new canonical FTO report (who was off, paid /
 *                       unpaid, hours, balance impact, reconciled days).
 *   2. COA Reports    — the new per-day COA report (window, duration, and
 *                       its real payroll effect: salaried gap-fill,
 *                       hourly pays 0).
 *   3. Payroll / Punch Time Records — the existing generators, grouped:
 *                       Payroll time card, Punch grid, Salaried, Hourly,
 *                       Attendance report. All unchanged components.
 *   4. Analytics      — the charts dashboard, full width (kept separate
 *                       from generate-and-export reports per Round 2
 *                       feedback; same component, same ?report=analytics).
 *
 * Old ?report= keys keep working: salaried/hourly/punchgrid/attendance/
 * payroll resolve into category 3; analytics stays analytics; new keys
 * fto-report / coa-report select 1 and 2 (the shell passes ?report=
 * straight through, so every old bookmark still lands on its report).
 */
const LazySalaried   = React.lazy(() => import('../HrReportsSalaried'));
const LazyHourly     = React.lazy(() => import('../HrReportsHourly'));
const LazyPunchGrid  = React.lazy(() => import('../PunchGridReport'));
const LazyAttendance = React.lazy(() => import('../AttendanceReport'));
const LazyPayroll    = React.lazy(() => import('../PayrollTimeCard'));
const LazyAnalytics  = React.lazy(() => import('../AttendanceAnalytics'));
const LazyFtoReport  = React.lazy(() => import('../FtoReportPage'));
const LazyCoaReport  = React.lazy(() => import('../CoaReportPage'));

// Category 3's inner generators (existing pages, untouched).
const PAYROLL_ITEMS = [
    { key: 'payroll',    label: 'Payroll time card', icon: 'ri-money-dollar-circle-line', Comp: LazyPayroll },
    { key: 'punchgrid',  label: 'Punch grid',        icon: 'ri-grid-line',                Comp: LazyPunchGrid },
    { key: 'salaried',   label: 'Salaried report',   icon: 'ri-file-text-line',           Comp: LazySalaried },
    { key: 'hourly',     label: 'Hourly report',     icon: 'ri-file-text-line',           Comp: LazyHourly },
    { key: 'attendance', label: 'Attendance report', icon: 'ri-calendar-todo-line',       Comp: LazyAttendance },
];
const PAYROLL_KEYS = PAYROLL_ITEMS.map((i) => i.key);

const CATEGORIES = [
    { key: 'fto-report', label: 'FTO reports', icon: 'ri-calendar-2-line',
      desc: 'Flexible Time Off — paid/unpaid, hours, balance impact.' },
    { key: 'coa-report', label: 'COA reports', icon: 'ri-calendar-check-line',
      desc: 'Change of Attendance — windows, durations, payroll effect.' },
    { key: 'payroll',    label: 'Payroll / punch time records', icon: 'ri-money-dollar-circle-line',
      desc: 'Time card, punch grid, salaried/hourly, attendance.' },
    { key: 'analytics',  label: 'Analytics', icon: 'ri-line-chart-line',
      desc: 'Charts & trends — opens full width.' },
];

const HrReportsSection = ({ report, onReportChange }) => {
    // Resolve the ?report= key → category (old keys keep working).
    const category =
        report === 'fto-report' ? 'fto-report'
        : report === 'coa-report' ? 'coa-report'
        : report === 'analytics' ? 'analytics'
        : PAYROLL_KEYS.includes(report) ? 'payroll'
        : 'fto-report';
    const payrollItem = PAYROLL_ITEMS.find((i) => i.key === report) || PAYROLL_ITEMS[0];
    const PayrollComp = payrollItem.Comp;

    if (category === 'analytics') {
        return (
            <div>
                <div className="hr-section-title">
                    <div>
                        <h5>Attendance analytics</h5>
                        <span className="text-muted">Charts &amp; trends across attendance data.</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-soft-primary"
                        onClick={() => onReportChange('fto-report')}>
                        <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />
                        Back to reports
                    </button>
                </div>
                <HrEmbeddedPage><LazyAnalytics /></HrEmbeddedPage>
            </div>
        );
    }

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Reports</h5>
                    <span className="text-muted">Four categories — pick one, set filters, generate or export.</span>
                </div>
            </div>

            {/* Category strip — four cards, always visible. */}
            <div className="hr-report-cats" role="tablist" aria-label="Report categories">
                {CATEGORIES.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        role="tab"
                        aria-selected={category === c.key}
                        className={`hr-analytics-card ${category === c.key ? 'active' : ''}`}
                        onClick={() => onReportChange(c.key === 'payroll' ? 'payroll' : c.key)}
                    >
                        <i className={c.icon} aria-hidden="true" />
                        <span className="hr-analytics-card-text">
                            <strong>{c.label}</strong>
                            <small>{c.desc}</small>
                        </span>
                        <i className="ri-arrow-right-s-line ms-auto" aria-hidden="true" />
                    </button>
                ))}
            </div>

            {category === 'fto-report' && <HrEmbeddedPage><LazyFtoReport /></HrEmbeddedPage>}
            {category === 'coa-report' && <HrEmbeddedPage><LazyCoaReport /></HrEmbeddedPage>}

            {category === 'payroll' && (
                <div className="hr-sidegrid">
                    <SideNavTabs
                        tabs={PAYROLL_ITEMS.map((r) => ({ key: r.key, label: r.label, icon: r.icon }))}
                        activeKey={payrollItem.key}
                        onChange={onReportChange}
                        width={220}
                        ariaLabel="Payroll and punch time records"
                    />
                    <HrEmbeddedPage><PayrollComp /></HrEmbeddedPage>
                </div>
            )}
        </div>
    );
};

export default HrReportsSection;
